package ui.login

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInParent
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import linx.linxapp.generated.resources.Res
import linx.linxapp.generated.resources.coffee_shop
import linx.linxapp.generated.resources.primary_logo_final
import openBrowserWithUrl
import org.jetbrains.compose.resources.painterResource
import ui.Colors
import ui.PhoneEntryField

fun Modifier.firstOrIntermediateFlowRowItem(
    previousItemY: Float,
    onLineCountIterate: () -> Unit,
    onNewItemCalculated: (Float) -> Unit,
): Modifier {
    return this.then(
        Modifier.onGloballyPositioned { coordinates ->
            val itemY = coordinates.positionInParent().y
            if (itemY != previousItemY && previousItemY != 0f) {
                onLineCountIterate()
            }
            onNewItemCalculated(itemY)
        }
    )
}

fun Modifier.lastFlowRowItem(
    previousItemY: Float,
    onLineCountIterate: () -> Unit,
    onNewItemCalculated: (Float) -> Unit
): Modifier {
    return this.then(
        Modifier.onGloballyPositioned { coordinates ->
            val itemY = coordinates.positionInParent().y
            if (itemY != previousItemY) {
                onLineCountIterate()
            }
            onNewItemCalculated(itemY)
        }
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun LoginRequest(
    loginViewModel: LoginViewModel = loginViewModel(),
) {
    var lineCount by remember { mutableStateOf(1) }
    var previousItemY by remember { mutableStateOf(0f) }
    var isFirstItem by remember { mutableStateOf(true) }
    var parentColumnHeight by remember { mutableStateOf(0.dp) }
    val uriHandler = LocalUriHandler.current
    val padding = 16.dp

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .onGloballyPositioned {
                parentColumnHeight = it.boundsInWindow().height.dp - padding * 2
            }
    ) {
        FlowRow(
            modifier = Modifier
                .onGloballyPositioned {
                    if (!isFirstItem) {
                        previousItemY = 0f
                        lineCount = 1
                    }
                    isFirstItem = false
                }
                .padding(padding),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            Column(
                horizontalAlignment = Alignment.Start,
                modifier = Modifier
                    .weight(1f)
                    .firstOrIntermediateFlowRowItem(previousItemY, { lineCount++ }) {
                        previousItemY = it
                    }
                    .then(
                        if (lineCount > 1) {
                            Modifier
                        } else {
                            Modifier.height(parentColumnHeight)
                        }
                    )
            ) {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = if (lineCount > 1) Alignment.Center else Alignment.CenterStart
                ) {
                    Image(
                        painterResource(Res.drawable.primary_logo_final),
                        contentDescription = null,
                        modifier = Modifier.width(100.dp),
                    )
                }
                Box(
                    contentAlignment = Alignment.Center,
                    modifier = Modifier.fillMaxSize()
                ) {
                    Column(
                        verticalArrangement = Arrangement.Center,
                        modifier = Modifier
                            .widthIn(0.dp, 500.dp)
                            .padding(32.dp)
                    ) {
                        Text(
                            text = "Welcome to your rewards",
                            fontSize = 26.sp,
                            fontWeight = FontWeight.Bold,
                            modifier = Modifier.padding(vertical = 8.dp)
                        )
                        Text(
                            "Login with your phone number",
                            textAlign = TextAlign.Center,
                            fontSize = 12.sp
                        )
                        Row(modifier = Modifier.height(IntrinsicSize.Min).padding(vertical = 8.dp)) {
                            CountryCodeDropDown(
                                countryCodeStateFlow = loginViewModel.countryCodeStateFlow,
                                onCountryCodeChanged = loginViewModel::countryCodeSelected
                            )
                            PhoneEntryField(
                                phoneStateFlow = loginViewModel.phoneNumberState,
                                onPhoneNumberChanged = loginViewModel::updatePhoneNumber,
                                onEnterPressed = { loginViewModel.submitPhoneNumber() },
                            )
                        }
                        val isButtonEnable by loginViewModel.isButtonEnableStateFlow.collectAsState()
                        Button(
                            onClick = { loginViewModel.submitPhoneNumber() },
                            enabled = isButtonEnable,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = Colors.LoginGreen,
                                disabledContainerColor = Colors.Disabled,
                                disabledContentColor = Color.Black.copy(.7f),
                                contentColor = Color.Black
                            )
                        ) {
                            Text("Log In")
                        }
                        Spacer(modifier = Modifier.height(32.dp))
                        Text("No rewards yet?", fontSize = 12.sp)
                        Text(
                            "Enroll by visiting businesses partnered with Linx to start collecting points with every purchase.",
                            fontSize = 12.sp
                        )
                    }
                }

            }
            Column(
                modifier = Modifier.weight(1f)
                    .lastFlowRowItem(previousItemY, { lineCount++ }) {
                        previousItemY = it
                    }
                    .then(
                        if (lineCount > 1) {
                            Modifier
                        } else {
                            Modifier.height(parentColumnHeight)
                        }
                    ),
            ) {
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = Colors.Secondary,
                    ),
                    modifier = Modifier
                        .then(
                        if (lineCount > 1) {
                            Modifier
                        } else {
                            Modifier.weight(1f)
                        }
                    )
                ) {
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        "No apps. No cards. No extra steps.",
                        color = Color.White,
                        fontSize = 26.sp,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.align(Alignment.CenterHorizontally).padding(horizontal = 16.dp),
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    Text(
                        "Just instant rewards",
                        fontSize = 22.sp,
                        color = Colors.LoginGreen,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.align(Alignment.CenterHorizontally).padding(horizontal = 16.dp)
                    )
                    Image(
                        painterResource(Res.drawable.coffee_shop),
                        contentDescription = null,
                        modifier = Modifier
                            .widthIn(0.dp, 500.dp)
                            .align(Alignment.CenterHorizontally),
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        "Earning loyalty perks from your favorite businesses has never been easier. " +
                            "Discover new local spots, access your e-receipts, " +
                            "and keep track of your points, all in one place.",
                        modifier = Modifier
                            .align(Alignment.CenterHorizontally)
                            .padding(horizontal = 32.dp),
                        color = Color.White,
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                }
                Spacer(modifier = Modifier.height(16.dp))
                Card(
                    border = BorderStroke(2.dp, Colors.LoginGreen),
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable {
                            uriHandler.openBrowserWithUrl("https://www.enrollandpay.com/contact/")
                        },
                    colors = CardDefaults.cardColors(
                        containerColor = Color.Transparent,
                    ),
                ) {
                    Text("Are you a business? Learn more about our card-linked loyalty program.", modifier = Modifier.padding(16.dp))
                }
            }
        }
    }
}